/**
 * Smart Slug Resolution System
 *
 * This utility handles the resolution of short slugs to their correct full paths
 * without requiring a full data migration. It works with existing Contentful data
 * and provides intelligent redirects.
 */

import type { Article } from '@/types/contentful'
import { PARENT_PATH_MAP } from '../config/slugMaps'

/**
 * Category ID to slug mapping
 */
const CATEGORY_SLUG_MAP: Record<string, string> = {
  'category-laga-vego': 'laga-vego',
  'category-engagera-dig-i-klubben': 'engagera-dig-i-klubben',
  'category-djuren-i-samhallet': 'djuren-i-samhallet',
  'category-pyssla-djurvanligt': 'pyssla-djurvanligt',
  'category-nar-djur-far-illa': 'nar-djur-far-illa',
  'category-djurvanliga-tips': 'djurvanliga-tips',
  'category-lar-dig-mer': 'lar-dig-mer',
  'category-for-vuxna': 'for-vuxna',
  'category-for-pedagoger': 'for-pedagoger',
}

/**
 * Resolve a short slug to its correct full path
 */
export const resolveSlugToPath = async (
  slug: string,
  currentPath: string[] = [],
): Promise<string | null> => {
  try {
    // Try to infer from current path structure
    if (currentPath.length >= 2) {
      const [firstPart, secondPart] = currentPath

      switch (firstPart) {
        case 'hjalp-djuren':
          // If current path looks like hjalp-djuren/category/slug, use it
          if (secondPart && secondPart !== slug) {
            return `/hjalp-djuren/${secondPart}/${slug}`
          }
          break
        case 'djurfakta':
          // If current path looks like djurfakta/slug, use it
          return `/djurfakta/${slug}`
        case 'klubbnytt':
          // If current path looks like klubbnytt/slug, use it
          return `/klubbnytt/${slug}`
        case 'om-oss':
          // If current path looks like om-oss/slug, use it
          return `/om-oss/${slug}`
        case 'vuxen':
          // If current path looks like vuxen/category/slug, use it
          if (secondPart && secondPart !== slug) {
            return `/vuxen/${secondPart}/${slug}`
          }
          break
      }
    }

    return null
  } catch (error) {
    console.error('Error resolving slug to path:', error)
    return null
  }
}

/**
 * Get the correct full path for an article based on its Contentful data
 */
export const getCorrectPathForArticle = async (
  article: Article,
  currentPath: string[] = [],
): Promise<string | null> => {
  try {
    // Use the sync generateArticlePath function first
    const generatedPath = generateArticlePath(article)

    // If it generated a path different from just the slug, use it
    if (generatedPath !== `/${article.fields.slug}`) {
      return generatedPath
    }

    // Fallback to slug resolution for edge cases
    return await resolveSlugToPath(article.fields.slug, currentPath)
  } catch (error) {
    console.error('Error determining correct path for article:', error)
    return null
  }
}

/**
 * Check if a path should redirect to a different path
 */
export const shouldRedirect = (currentPath: string, correctPath: string): boolean => {
  return currentPath !== correctPath && correctPath !== null
}

/**
 * Generate the correct path for an article based on its Contentful data
 * This is the main function to use for generating _path values
 */
export const generateArticlePath = (article: Article): string => {
  try {
    // Validate input
    if (!article?.fields?.slug) {
      console.warn('Article missing slug:', article)
      return '/'
    }

    // If article has a parent, determine the correct path based on parent type
    if (article.fields?.parent?.sys?.id) {
      const parentId = article.fields.parent.sys.id
      const parentPath = PARENT_PATH_MAP[parentId]

      if (parentPath) {
        switch (parentPath) {
          case 'djurfakta':
            // If it's a djurfakta article, use djurfakta path
            // Check if slug already contains the parent path to avoid duplication
            if (
              article.fields.slug.startsWith('djurfakta/') ||
              article.fields.slug.startsWith('/djurfakta/')
            ) {
              return article.fields.slug.startsWith('/')
                ? article.fields.slug
                : `/${article.fields.slug}`
            }
            return `/djurfakta/${article.fields.slug}`

          case 'klubbnytt':
            // If it's a klubbnytt article, use klubbnytt path
            // Check if slug already contains the parent path to avoid duplication
            if (
              article.fields.slug.startsWith('klubbnytt/') ||
              article.fields.slug.startsWith('/klubbnytt/')
            ) {
              return article.fields.slug.startsWith('/')
                ? article.fields.slug
                : `/${article.fields.slug}`
            }
            return `/klubbnytt/${article.fields.slug}`

          case 'om-oss':
            // If it's an om-oss article, use om-oss path
            // Check if slug already contains the parent path to avoid duplication
            if (
              article.fields.slug.startsWith('om-oss/') ||
              article.fields.slug.startsWith('/om-oss/')
            ) {
              return article.fields.slug.startsWith('/')
                ? article.fields.slug
                : `/${article.fields.slug}`
            }
            return `/om-oss/${article.fields.slug}`

          case 'vuxen':
            // If it's a vuxen article, we need to check the category
            if ((article.fields as any)?.category?.fields?.slug) {
              const categorySlug = (article.fields as any).category.fields.slug
              // Check if slug already contains the full path to avoid duplication
              const expectedPath = `vuxen/${categorySlug}/`
              if (
                article.fields.slug.startsWith(expectedPath) ||
                article.fields.slug.startsWith(`/${expectedPath}`)
              ) {
                return article.fields.slug.startsWith('/')
                  ? article.fields.slug
                  : `/${article.fields.slug}`
              }
              // Extract just the article name from the slug (last part after the last slash)
              const articleName = article.fields.slug.split('/').pop() || article.fields.slug
              const result = `/vuxen/${categorySlug}/${articleName}`
              return result
            }
            // If no category found, try to infer from the slug
            if (article.fields.slug.includes('vuxen-eller-pedagog/')) {
              // This is an old vuxen-eller-pedagog article, redirect to vuxen root
              const articleName = article.fields.slug.split('/').pop() || article.fields.slug
              return `/vuxen/${articleName}`
            }
            break

          case 'hjalp-djuren':
            // If it's a hjalp-djuren article, we need to check the category
            if ((article.fields as any)?.category?.sys?.id) {
              const categoryId = (article.fields as any).category.sys.id
              const categorySlug = CATEGORY_SLUG_MAP[categoryId]

              if (categorySlug) {
                // Check if slug already contains the full path to avoid duplication
                const expectedPath = `hjalp-djuren/${categorySlug}/`
                if (
                  article.fields.slug.startsWith(expectedPath) ||
                  article.fields.slug.startsWith(`/${expectedPath}`)
                ) {
                  return article.fields.slug.startsWith('/')
                    ? article.fields.slug
                    : `/${article.fields.slug}`
                }
                // Extract just the article name from the slug (last part after the last slash)
                const articleName = article.fields.slug.split('/').pop() || article.fields.slug
                return `/hjalp-djuren/${categorySlug}/${articleName}`
              }
            }
            break
        }
      }
    }

    // Fallback: just use the slug (for articles without proper relationships)
    const fallbackPath = `/${article.fields.slug}`
    return fallbackPath || '/'
  } catch (error) {
    console.error('Error generating article path:', error)
    return `/${article.fields.slug}` || '/'
  }
}

/**
 * Generate the correct path for a category based on its slug
 */
export const generateCategoryPath = (categorySlug: string): string => {
  try {
    // Validate input
    if (!categorySlug || typeof categorySlug !== 'string') {
      console.warn('Invalid category slug:', categorySlug)
      return '/'
    }

    // Check if it's a vuxen category
    if (categorySlug === 'for-vuxna' || categorySlug === 'for-pedagoger') {
      return `/vuxen/${categorySlug}`
    }
    // Default to hjalp-djuren for other categories
    return `/hjalp-djuren/${categorySlug}`
  } catch (error) {
    console.error('Error generating category path:', error)
    return '/'
  }
}
